import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import type * as React from 'react';
import { MotionProvider } from '../../motion';
import { type SurfaceDockEntry, useSurfaceDock } from './surface-dock-context';

function SurfaceDockItem({ entry }: { entry: SurfaceDockEntry }) {
	return (
		<motion.li
			layout
			initial={{ opacity: 0, y: 12, scale: 0.98 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={{ opacity: 0, y: 8, scale: 0.98 }}
			transition={{ duration: 0.18, ease: 'easeOut' }}
			className="corner-bevel overflow-hidden rounded-lg border border-primary/20 bg-background/90 shadow-black/20 shadow-lg backdrop-blur-md"
		>
			<div className="flex items-stretch">
				<button
					type="button"
					onClick={entry.restore}
					aria-label={m.surface_dock_restore({ title: entry.title })}
					className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-2.5 py-2 text-start transition-colors hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:outline-none"
				>
					<span className="corner-bevel grid size-8 shrink-0 place-items-center rounded-md border border-primary/20 bg-primary/5 text-primary">
						<Iconify
							icon={entry.icon ?? 'lucide:app-window'}
							className="size-4"
							aria-hidden="true"
						/>
					</span>
					<span className="flex min-w-0 flex-1 flex-col">
						<span className="truncate font-mono text-xs text-foreground">
							{entry.title}
						</span>
						{entry.caption ? (
							<span className="truncate font-mono text-2xs text-muted-foreground uppercase tracking-widest">
								{entry.caption}
							</span>
						) : null}
					</span>
					<Iconify
						icon="lucide:arrow-up-right"
						className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-focus-visible:opacity-100 group-hover:opacity-100"
						aria-hidden="true"
					/>
				</button>
				<button
					type="button"
					onClick={entry.close}
					aria-label={m.surface_dock_close({ title: entry.title })}
					className="grid w-9 shrink-0 cursor-pointer place-items-center border-border/60 border-s text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:outline-none"
				>
					<Iconify icon="lucide:x" className="size-3.5" aria-hidden="true" />
				</button>
			</div>
		</motion.li>
	);
}

export type SurfaceDockProps = React.ComponentProps<'section'>;

/**
 * The tray every minimized window lands in: a Messenger-style stack that
 * sits above the page and brings a surface back on click.
 *
 * Only rendered when a surface is minimized, so it never competes with the
 * page for a corner.
 */
export function SurfaceDock({ className, ...props }: SurfaceDockProps) {
	const { entries } = useSurfaceDock();

	const restoreAll = () => {
		for (const entry of [...entries]) entry.restore();
	};

	return (
		<MotionProvider feature="domMax">
			<div className="pointer-events-none fixed inset-x-0 bottom-0 z-60 flex justify-end px-3 pb-3 sm:px-4 sm:pb-4">
				<AnimatePresence>
					{entries.length > 0 ? (
						<motion.div
							key="surface-dock"
							initial={{ opacity: 0, y: 16 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: 16 }}
							transition={{ duration: 0.2, ease: 'easeOut' }}
							className="pointer-events-auto w-[min(20rem,calc(100vw-1.5rem))]"
						>
							<section
								aria-label={m.surface_dock_label()}
								className={cn('flex flex-col gap-2', className)}
								{...props}
							>
								{entries.length > 1 ? (
									<div className="flex items-center justify-between gap-2 px-1">
										<span className="truncate font-mono text-2xs text-muted-foreground uppercase tracking-widest">
											<span className="text-primary">~/</span>{' '}
											{m.surface_dock_title({ count: entries.length })}
										</span>
										<button
											type="button"
											onClick={restoreAll}
											className="shrink-0 cursor-pointer rounded-xs font-mono text-2xs text-muted-foreground uppercase tracking-widest transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:outline-none"
										>
											{m.surface_dock_restore_all()}
										</button>
									</div>
								) : null}
								<ul className="flex flex-col gap-1.5">
									<AnimatePresence initial={false}>
										{entries.map((entry) => (
											<SurfaceDockItem key={entry.id} entry={entry} />
										))}
									</AnimatePresence>
								</ul>
							</section>
						</motion.div>
					) : null}
				</AnimatePresence>
			</div>
		</MotionProvider>
	);
}
